import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MultiStepForm = ({ isOpen, onClose, language = 'fr', preSelectedService = null }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Section 1
    fullName: '',
    email: '',
    phone: '',
    city: '',
    // Section 2
    reason: [],
    // Section 3
    services: [],
    // Section 4
    currentStyle: [],
    desiredStyle: [],
    // Section 5
    height: '',
    bodyShape: '',
    // Section 6
    budget: '',
    // Section 7
    consultationFormat: '',
    availability: [],
    howDidYouHear: '',
    // Section 8
    consent: false,
    photoConsent: false,
    newsletterConsent: false
  });

  // Reset form completely every time the modal opens (fresh session).
  // Fixes bug: previously, state persisted between openings, causing old answers
  // to pre-fill + isSubmitting getting stuck after an error.
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setIsSubmitting(false);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        city: '',
        reason: [],
        services: preSelectedService ? [preSelectedService] : [],
        currentStyle: [],
        desiredStyle: [],
        height: '',
        bodyShape: '',
        budget: '',
        consultationFormat: '',
        availability: [],
        howDidYouHear: '',
        consent: false,
        photoConsent: false,
        newsletterConsent: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preSelectedService]);


  const translations = {
    fr: {
      close: 'Fermer',
      next: 'Suivant',
      previous: 'Précédent',
      submit: 'Envoyer',
      required: 'Champ requis',
      intro: {
        title: 'Formulaire de pré-consultation — Glow & You',
        welcome: 'Merci de prendre quelques minutes pour remplir ce formulaire. Les informations recueillies permettront à Glow & You d\'analyser votre situation afin de vous proposer le service le plus adapté à vos besoins, à votre style et à votre budget.',
        steps: [
          '1. Remplir ce formulaire',
          '2. Analyse personnalisée par Glow & You',
          '3. Envoi d\'un lien de réservation'
        ]
      },
      sections: {
        personalInfo: {
          title: 'Informations Personnelles',
          fullName: 'Nom et prénom',
          email: 'Adresse courriel',
          phone: 'Numéro de téléphone',
          city: 'Ville et pays'
        },
        need: {
          title: 'Votre Besoin',
          question: 'Pourquoi faites-vous appel à Glow & You?',
          options: [
            'Changement de style',
            'Confiance en soi',
            'Travail',
            'Événements',
            'Nouvelle étape de vie',
            'Autre'
          ]
        },
        services: {
          title: 'Service Désiré',
          description: 'Choisissez le ou les services qui vous intéressent.',
          options: [
            'Éveil de ton Image',
            'Harmonie & Style',
            'Révélation Glow & You',
            'Éclat Mariée',
            'À la carte : Analyse de Style',
            'À la carte : Colorimétrie',
            'À la carte : Morphologie',
            'À la carte : Audit du Dressing',
            'À la carte : Tri du Dressing',
            'À la carte : Garde-Robe Capsule',
            'À la carte : Magasinage Accompagné',
            'À la carte : Tenue Événement',
            'À la carte : Atelier Individuel',
            'À la carte : Atelier en Groupe',
            'Pas encore décidée'
          ]
        },
        style: {
          title: 'Votre Style',
          current: 'Style actuel',
          currentOptions: ['Classique', 'Décontracté', 'Minimaliste', 'Indéfini'],
          desired: 'Style désiré',
          desiredOptions: ['Élégant', 'Professionnel', 'Féminin', 'Audacieux', 'Naturel', 'Autre']
        },
        morphology: {
          title: 'Morphologie',
          description: 'Ces informations nous aident à analyser votre silhouette afin de vous proposer des coupes et styles qui mettent naturellement votre corps en valeur.',
          height: 'Taille (en cm)',
          bodyShape: 'Silhouette',
          shapes: [
            { value: 'A', label: 'A — épaules étroites, hanches larges' },
            { value: 'H', label: 'H — épaules et hanches égales' },
            { value: 'V', label: 'V — épaules larges, hanches étroites' },
            { value: 'O', label: 'O — silhouette arrondie' },
            { value: '8', label: '8 — taille bien marquée' },
            { value: 'unknown', label: 'Je ne sais pas' }
          ]
        },
        budget: {
          title: 'Budget',
          description: 'Comprendre votre budget nous permet de vous proposer des recommandations réalistes et adaptées.',
          options: [
            'Moins de 200$',
            '200$ - 500$',
            '500$ - 1000$',
            'Plus de 1000$'
          ]
        },
        preferences: {
          title: 'Préférences',
          description: 'Cette section nous permet d\'adapter l\'expérience à vos contraintes et disponibilités.',
          format: 'Format de consultation',
          formatOptions: ['Visio', 'Présentiel', 'Les deux'],
          availability: 'Disponibilité',
          availabilityOptions: [
            'Semaine en journée',
            'Semaine en soirée',
            'Fin de semaine'
          ],
          howDidYouHear: 'Comment avez-vous entendu parler de nous?',
          howOptions: ['Instagram', 'Bouche à oreille', 'Site web', 'Autre']
        },
        consent: {
          title: 'Consentement',
          note: 'Des photos pourront être demandées après la prise de rendez-vous pour analyse plus précise.',
          text: 'J\'accepte que les informations fournies soient utilisées par Glow & You pour analyser ma demande et proposer un service adapté.'
        }
      },
      success: {
        title: 'Merci pour votre confiance !',
        message: 'Nous avons bien reçu votre formulaire et nous vous contacterons dans les 48 heures.',
        subtitle: 'Bienvenue dans la famille Glow & You. Votre transformation commence maintenant.'
      }
    },
    en: {
      close: 'Close',
      next: 'Next',
      previous: 'Previous',
      submit: 'Submit',
      required: 'Required field',
      intro: {
        title: 'Pre-Consultation Form — Glow & You',
        welcome: 'Thank you for taking a few minutes to complete this form. The information collected will allow Glow & You to analyze your situation and offer you the service best suited to your needs, style and budget.',
        steps: [
          '1. Fill out this form',
          '2. Personalized analysis by Glow & You',
          '3. Booking link sent'
        ]
      },
      sections: {
        personalInfo: {
          title: 'Personal Information',
          fullName: 'Full Name',
          email: 'Email Address',
          phone: 'Phone Number',
          city: 'City and Country'
        },
        need: {
          title: 'Your Need',
          question: 'Why are you reaching out to Glow & You?',
          options: [
            'Style Change',
            'Self-Confidence',
            'Work',
            'Events',
            'New Life Stage',
            'Other'
          ]
        },
        services: {
          title: 'Desired Service',
          description: 'Choose the service(s) that interest you.',
          options: [
            'Find Your Glow',
            'Style in Harmony',
            'Total Glow',
            'Bridal Glow',
            'À la carte: Style Analysis',
            'À la carte: Color Analysis',
            'À la carte: Body Shape Analysis',
            'À la carte: Wardrobe Audit',
            'À la carte: Wardrobe Edit',
            'À la carte: Capsule Wardrobe',
            'À la carte: Personal Shopping',
            'À la carte: Event Styling',
            'À la carte: Individual Workshop',
            'À la carte: Group Workshop',
            'Not sure yet'
          ]
        },
        style: {
          title: 'Your Style',
          current: 'Current Style',
          currentOptions: ['Classic', 'Casual', 'Minimalist', 'Undefined'],
          desired: 'Desired Style',
          desiredOptions: ['Elegant', 'Professional', 'Feminine', 'Bold', 'Natural', 'Other']
        },
        morphology: {
          title: 'Body Shape',
          description: 'This information helps us analyze your silhouette to suggest cuts and styles that naturally flatter your body.',
          height: 'Height (in cm)',
          bodyShape: 'Silhouette',
          shapes: [
            { value: 'A', label: 'A — narrow shoulders, wide hips' },
            { value: 'H', label: 'H — equal shoulders and hips' },
            { value: 'V', label: 'V — wide shoulders, narrow hips' },
            { value: 'O', label: 'O — rounded silhouette' },
            { value: '8', label: '8 — defined waist' },
            { value: 'unknown', label: 'I don\'t know' }
          ]
        },
        budget: {
          title: 'Budget',
          description: 'Understanding your budget allows us to offer realistic and tailored recommendations.',
          options: [
            'Less than $200',
            '$200 - $500',
            '$500 - $1000',
            'More than $1000'
          ]
        },
        preferences: {
          title: 'Preferences',
          description: 'This section allows us to adapt the experience to your constraints and availability.',
          format: 'Consultation Format',
          formatOptions: ['Virtual', 'In-person', 'Both'],
          availability: 'Availability',
          availabilityOptions: [
            'Weekday daytime',
            'Weekday evening',
            'Weekend'
          ],
          howDidYouHear: 'How did you hear about us?',
          howOptions: ['Instagram', 'Word of mouth', 'Website', 'Other']
        },
        consent: {
          title: 'Consent',
          note: 'Photos may be requested after booking for more precise analysis.',
          text: 'I agree that the information provided will be used by Glow & You to analyze my request and offer an appropriate service.'
        }
      },
      success: {
        title: 'Thank you for your trust!',
        message: 'We have received your form and will contact you within 48 hours.',
        subtitle: 'Welcome to the Glow & You family. Your transformation starts now.'
      }
    }
  };

  const t = translations[language];

  const totalSteps = 9; // Intro + 7 sections + Success

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData((prev) => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return {
          ...prev,
          [field]: current.filter((item) => item !== value)
        };
      } else {
        return {
          ...prev,
          [field]: [...current, value]
        };
      }
    });
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1: // Personal Info
        if (!formData.fullName || !formData.email || !formData.phone) {
          toast.error(t.required);
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          toast.error('Email invalide');
          return false;
        }
        return true;
      case 2: // Need
        if (formData.reason.length === 0) {
          toast.error(t.required);
          return false;
        }
        return true;
      case 3: // Services - NON REQUIS si service pré-sélectionné
        if (preSelectedService) {
          return true; // Skip validation - service déjà capturé
        }
        if (formData.services.length === 0) {
          toast.error(t.required);
          return false;
        }
        return true;
      case 5: // Morphology
        if (!formData.height || !formData.bodyShape) {
          toast.error(t.required);
          return false;
        }
        return true;
      case 6: // Budget
        if (!formData.budget) {
          toast.error(t.required);
          return false;
        }
        return true;
      case 7: // Preferences
        if (!formData.consultationFormat || formData.availability.length === 0) {
          toast.error(t.required);
          return false;
        }
        return true;
      case 8: // Consent
        if (!formData.consent) {
          toast.error('Vous devez accepter les conditions');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep === 8) {
        handleSubmit();
      } else {
        setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
      }
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/consultation-form`, formData);
      if (response.data.status === 'success') {
        // Move to success screen (step 9)
        setCurrentStep(totalSteps - 1);
        
        // Show success toast
        toast.success(
          language === 'fr' 
            ? 'Formulaire envoyé avec succès!' 
            : 'Form submitted successfully!'
        );
        
        // Reset form data after a delay
        setTimeout(() => {
          setFormData({
            fullName: '',
            email: '',
            phone: '',
            city: '',
            reason: [],
            services: [],
            currentStyle: [],
            desiredStyle: [],
            height: '',
            bodyShape: '',
            budget: '',
            consultationFormat: '',
            availability: [],
            howDidYouHear: '',
            consent: false,
            photoConsent: false,
            newsletterConsent: false
          });
        }, 2000);
      } else {
        toast.error(
          language === 'fr'
            ? 'Une erreur est survenue'
            : 'An error occurred'
        );
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(
        language === 'fr'
          ? 'Erreur lors de l\'envoi du formulaire'
          : 'Error submitting form'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderProgressBar = () => {
    const progress = ((currentStep + 1) / totalSteps) * 100;
    return (
      <div className="w-full h-1 bg-[#F5F3F0] mb-8">
        <div
          className="h-full bg-[#8B4513] transition-all duration-300"
          style={{ width: `${progress}%` }}
        ></div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="text-center max-w-2xl mx-auto">
            <h2
              className="text-4xl font-light text-[#2C2C2C] mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t.intro.title}
            </h2>
            <p className="text-lg text-[#8B7D6B] mb-8 leading-relaxed">
              {t.intro.welcome}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              {t.intro.steps.map((step, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-[#C4B5A0] text-white flex items-center justify-center mb-3">
                    {index + 1}
                  </div>
                  <p className="text-[#2C2C2C]">{step}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 1:
        return (
          <div className="max-w-xl mx-auto">
            <h2
              className="text-3xl font-light text-[#2C2C2C] mb-8"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t.sections.personalInfo.title}
            </h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  {t.sections.personalInfo.fullName} *
                </label>
                <input
                  type="text"
                  value={formData.fullName || ''}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  placeholder="Ex: Marie Beauchamp"
                  className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#D4C4B0] rounded-md text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4B5A0]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  {t.sections.personalInfo.email} *
                </label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Ex: marie@exemple.com"
                  className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#D4C4B0] rounded-md text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4B5A0]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  {t.sections.personalInfo.phone} *
                </label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Ex: (343) 262-8604"
                  className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#D4C4B0] rounded-md text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4B5A0]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  {t.sections.personalInfo.city}
                </label>
                <input
                  type="text"
                  value={formData.city || ''}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="Ex: Ottawa, Canada"
                  className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#D4C4B0] rounded-md text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4B5A0]"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="max-w-xl mx-auto">
            <h2
              className="text-3xl font-light text-[#2C2C2C] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t.sections.need.title}
            </h2>
            <p className="text-lg text-[#8B7D6B] mb-8">{t.sections.need.question} *</p>
            <div className="space-y-3">
              {t.sections.need.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleMultiSelect('reason', option)}
                  className={`w-full p-4 text-left rounded-md border-2 transition-all ${
                    formData.reason.includes(option)
                      ? 'border-[#8B4513] bg-[#8B4513]/10'
                      : 'border-[#D4C4B0] hover:border-[#C4B5A0]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[#2C2C2C]">{option}</span>
                    {formData.reason.includes(option) && (
                      <Check className="w-5 h-5 text-[#8B4513]" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        // Si un service est pré-sélectionné, afficher en lecture seule
        if (preSelectedService) {
          return (
            <div className="max-w-2xl mx-auto">
              <h2
                className="text-3xl font-light text-[#2C2C2C] mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {t.sections.services.title}
              </h2>
              <p className="text-lg text-[#8B7D6B] mb-8">
                {language === 'fr' 
                  ? 'Votre service a été sélectionné automatiquement.' 
                  : 'Your service has been automatically selected.'}
              </p>
              <div className="p-6 bg-[#8B4513]/10 border-2 border-[#8B4513] rounded-md">
                <div className="flex items-center gap-3">
                  <Check className="w-6 h-6 text-[#8B4513]" />
                  <span className="text-lg font-medium text-[#2C2C2C]">{preSelectedService}</span>
                </div>
              </div>
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  {language === 'fr' 
                    ? '✓ Vous pouvez passer à l\'étape suivante en cliquant sur "Suivant".' 
                    : '✓ You can proceed to the next step by clicking "Next".'}
                </p>
              </div>
            </div>
          );
        }
        
        // Sinon, afficher la sélection normale
        return (
          <div className="max-w-2xl mx-auto">
            <h2
              className="text-3xl font-light text-[#2C2C2C] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t.sections.services.title}
            </h2>
            <p className="text-lg text-[#8B7D6B] mb-8">{t.sections.services.description}</p>
            <div className="space-y-3">
              {t.sections.services.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleMultiSelect('services', option)}
                  className={`w-full p-4 text-left rounded-md border-2 transition-all ${
                    formData.services.includes(option)
                      ? 'border-[#8B4513] bg-[#8B4513]/10'
                      : 'border-[#D4C4B0] hover:border-[#C4B5A0]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[#2C2C2C]">{option}</span>
                    {formData.services.includes(option) && (
                      <Check className="w-5 h-5 text-[#8B4513]" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="max-w-xl mx-auto">
            <h2
              className="text-3xl font-light text-[#2C2C2C] mb-8"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t.sections.style.title}
            </h2>
            <div className="space-y-8">
              <div>
                <label className="block text-lg font-medium text-[#2C2C2C] mb-4">
                  {t.sections.style.current}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {t.sections.style.currentOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleMultiSelect('currentStyle', option)}
                      className={`p-3 text-center rounded-md border-2 transition-all ${
                        formData.currentStyle.includes(option)
                          ? 'border-[#8B4513] bg-[#8B4513]/10'
                          : 'border-[#D4C4B0] hover:border-[#C4B5A0]'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-lg font-medium text-[#2C2C2C] mb-4">
                  {t.sections.style.desired}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {t.sections.style.desiredOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleMultiSelect('desiredStyle', option)}
                      className={`p-3 text-center rounded-md border-2 transition-all ${
                        formData.desiredStyle.includes(option)
                          ? 'border-[#8B4513] bg-[#8B4513]/10'
                          : 'border-[#D4C4B0] hover:border-[#C4B5A0]'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="max-w-xl mx-auto">
            <h2
              className="text-3xl font-light text-[#2C2C2C] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t.sections.morphology.title}
            </h2>
            <p className="text-lg text-[#8B7D6B] mb-8">{t.sections.morphology.description}</p>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  {t.sections.morphology.height} *
                </label>
                <input
                  type="number"
                  value={formData.height}
                  onChange={(e) => handleInputChange('height', e.target.value)}
                  placeholder="165"
                  className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#D4C4B0] rounded-md text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4B5A0]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-4">
                  {t.sections.morphology.bodyShape} *
                </label>
                <div className="space-y-3">
                  {t.sections.morphology.shapes.map((shape) => (
                    <button
                      key={shape.value}
                      onClick={() => handleInputChange('bodyShape', shape.value)}
                      className={`w-full p-4 text-left rounded-md border-2 transition-all ${
                        formData.bodyShape === shape.value
                          ? 'border-[#8B4513] bg-[#8B4513]/10'
                          : 'border-[#D4C4B0] hover:border-[#C4B5A0]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[#2C2C2C]">{shape.label}</span>
                        {formData.bodyShape === shape.value && (
                          <Check className="w-5 h-5 text-[#8B4513]" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="max-w-xl mx-auto">
            <h2
              className="text-3xl font-light text-[#2C2C2C] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t.sections.budget.title}
            </h2>
            <p className="text-lg text-[#8B7D6B] mb-8">{t.sections.budget.description}</p>
            <div className="space-y-3">
              {t.sections.budget.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleInputChange('budget', option)}
                  className={`w-full p-4 text-left rounded-md border-2 transition-all ${
                    formData.budget === option
                      ? 'border-[#8B4513] bg-[#8B4513]/10'
                      : 'border-[#D4C4B0] hover:border-[#C4B5A0]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[#2C2C2C]">{option}</span>
                    {formData.budget === option && (
                      <Check className="w-5 h-5 text-[#8B4513]" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 7:
        return (
          <div className="max-w-xl mx-auto">
            <h2
              className="text-3xl font-light text-[#2C2C2C] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t.sections.preferences.title}
            </h2>
            <p className="text-lg text-[#8B7D6B] mb-8">{t.sections.preferences.description}</p>
            <div className="space-y-8">
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-4">
                  {t.sections.preferences.format} *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {t.sections.preferences.formatOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleInputChange('consultationFormat', option)}
                      className={`p-3 text-center rounded-md border-2 transition-all ${
                        formData.consultationFormat === option
                          ? 'border-[#8B4513] bg-[#8B4513]/10'
                          : 'border-[#D4C4B0] hover:border-[#C4B5A0]'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-4">
                  {t.sections.preferences.availability} *
                </label>
                <div className="space-y-3">
                  {t.sections.preferences.availabilityOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleMultiSelect('availability', option)}
                      className={`w-full p-3 text-left rounded-md border-2 transition-all ${
                        formData.availability.includes(option)
                          ? 'border-[#8B4513] bg-[#8B4513]/10'
                          : 'border-[#D4C4B0] hover:border-[#C4B5A0]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[#2C2C2C]">{option}</span>
                        {formData.availability.includes(option) && (
                          <Check className="w-5 h-5 text-[#8B4513]" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  {t.sections.preferences.howDidYouHear}
                </label>
                <select
                  value={formData.howDidYouHear}
                  onChange={(e) => handleInputChange('howDidYouHear', e.target.value)}
                  className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#D4C4B0] rounded-md text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4B5A0]"
                >
                  <option value="">Sélectionnez...</option>
                  {t.sections.preferences.howOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="max-w-xl mx-auto">
            <h2
              className="text-3xl font-light text-[#2C2C2C] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t.sections.consent.title}
            </h2>
            <p className="text-sm text-[#8B7D6B] mb-8">{t.sections.consent.note}</p>
            
            <div className="space-y-4">
              {/* Required consent */}
              <div className="bg-[#F5F3F0] p-6 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.consent}
                    onChange={(e) => handleInputChange('consent', e.target.checked)}
                    className="mt-1 w-5 h-5 text-[#8B4513] border-[#D4C4B0] rounded focus:ring-[#C4B5A0]"
                  />
                  <span className="text-[#2C2C2C]">{t.sections.consent.text}</span>
                </label>
              </div>

              {/* Optional photo consent */}
              <div className="bg-white p-6 rounded-lg border-2 border-[#F5F3F0]">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.photoConsent}
                    onChange={(e) => handleInputChange('photoConsent', e.target.checked)}
                    className="mt-1 w-5 h-5 text-[#8B4513] border-[#D4C4B0] rounded focus:ring-[#C4B5A0]"
                  />
                  <span className="text-sm text-[#2C2C2C]">
                    J'accepte que Glow & You puisse utiliser mes photos de séance et résultats avant/après à des fins promotionnelles sur son site web et ses réseaux sociaux. Je comprends que je peux retirer ce consentement à tout moment en contactant{' '}
                    <a href="mailto:contact@glowyou.org" className="text-[#8B4513] hover:underline">
                      contact@glowyou.org
                    </a>
                  </span>
                </label>
              </div>

              {/* Optional newsletter consent */}
              <div className="bg-white p-6 rounded-lg border-2 border-[#F5F3F0]">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.newsletterConsent}
                    onChange={(e) => handleInputChange('newsletterConsent', e.target.checked)}
                    className="mt-1 w-5 h-5 text-[#8B4513] border-[#D4C4B0] rounded focus:ring-[#C4B5A0]"
                  />
                  <span className="text-sm text-[#2C2C2C]">
                    J'accepte de recevoir des nouvelles, conseils et offres exclusives de Glow & You par courriel. Je peux me désabonner à tout moment.
                  </span>
                </label>
              </div>
            </div>
          </div>
        );

      case 9:
        return (
          <div className="text-center max-w-2xl mx-auto">
            <div className="w-20 h-20 bg-[#8B4513] rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2
              className="text-4xl font-light text-[#2C2C2C] mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t.success.title}
            </h2>
            <p className="text-lg text-[#8B7D6B] mb-4">{t.success.message}</p>
            <p className="text-lg text-[#2C2C2C] font-medium mb-8">{t.success.subtitle}</p>
            <button
              onClick={() => {
                onClose();
                // Reset to step 0 when closing
                setTimeout(() => setCurrentStep(0), 300);
              }}
              className="px-8 py-4 text-base font-medium tracking-wider uppercase text-white bg-[#8B4513] rounded-md hover:bg-[#A0522D] transition-all"
            >
              {t.close}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-[#D4C4B0] p-6 flex items-center justify-between z-10">
            <div className="text-2xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Glow & You
            </div>
            <button
              onClick={onClose}
              className="text-[#8B7D6B] hover:text-[#2C2C2C] transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Bar */}
          {renderProgressBar()}

          {/* Content */}
          <div className="p-8">{renderStepContent()}</div>

          {/* Navigation */}
          {currentStep !== 9 && (
            <div className="sticky bottom-0 bg-white border-t border-[#D4C4B0] p-6 flex items-center justify-between">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 0 || isSubmitting}
                className="flex items-center gap-2 px-6 py-3 text-[#8B7D6B] hover:text-[#2C2C2C] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
                {t.previous}
              </button>
              <button
                onClick={handleNext}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-3 bg-[#8B4513] text-white rounded-md hover:bg-[#A0522D] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {currentStep === 8 ? (
                  isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {language === 'fr' ? 'Envoi...' : 'Sending...'}
                    </>
                  ) : (
                    t.submit
                  )
                ) : (
                  <>
                    {t.next}
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiStepForm;
